"use client";

import { useState } from "react";
import { Checkbox } from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  type ScriptMode,
  toSubscript,
  toSuperscript,
} from "@/lib/tool-kit/text/super-sub";

/** English strings. A translated content file overrides these through `ui`. */
export const SUPERSCRIPT_GENERATOR_STRINGS = {
  superscriptLabel: "Superscript",
  subscriptLabel: "Subscript",
  superscriptHint: "Try: 2, -3 or st (only the part to raise)",
  subscriptHint: "Try: 2 or n (only the part to lower)",
  superscriptDigitsHint: "Try: x2, Fe3+ or E=mc2",
  subscriptDigitsHint: "Try: H2O, Fe2O3 or Ca(OH)2",
  digitsOnlyLabel:
    "Digits and charges only: letters stay normal, so whole formulas and exponents work",
  digitsOnlyNote:
    "In this mode a number in front of a capital letter or bracket (the 2 in 2H2O) is kept as a coefficient, and a + or - between two terms (x2+y2) is not raised.",
  resultPlaceholder: "Your result will appear here",
  copy: "Copy",
  copied: "Copied!",
} as const;

function ConverterCard({
  copied,
  exampleHint,
  id,
  input,
  label,
  onCopy,
  onInputChange,
  output,
  placeholder,
  copyLabel,
  copiedLabel,
}: {
  copied: boolean;
  exampleHint: string;
  id: string;
  input: string;
  label: string;
  onCopy: () => void;
  onInputChange: (value: string) => void;
  output: string;
  placeholder: string;
  copyLabel: string;
  copiedLabel: string;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-5">
      <label className="font-medium text-foreground text-sm" htmlFor={id}>
        {label}
      </label>
      <textarea
        className="min-h-[80px] w-full resize-y rounded-lg border border-border bg-background p-3 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        id={id}
        onChange={(e) => onInputChange(e.target.value)}
        placeholder={exampleHint}
        value={input}
      />
      <div className="flex items-center justify-between gap-3">
        <p className="min-h-[1.5rem] flex-1 whitespace-pre-wrap break-words text-base text-foreground leading-relaxed">
          {input.length > 0 ? output : placeholder}
        </p>
        <button
          className="rounded-lg border border-border bg-background px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={onCopy}
          type="button"
        >
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
    </div>
  );
}

export default function SuperscriptGeneratorWidget() {
  const [superInput, setSuperInput] = useState("");
  const [subInput, setSubInput] = useState("");
  const [copiedCard, setCopiedCard] = useState<string | null>(null);
  const [digitsOnly, setDigitsOnly] = useState(false);

  const strings = useToolUi(SUPERSCRIPT_GENERATOR_STRINGS);

  const mode: ScriptMode = digitsOnly ? "digits" : "all";
  const superOutput = toSuperscript(superInput, mode);
  const subOutput = toSubscript(subInput, mode);

  function handleCopy(card: string, value: string) {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedCard(card);
      setTimeout(() => setCopiedCard(null), 2000);
    });
  }

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <Checkbox
          checked={digitsOnly}
          label={strings.digitsOnlyLabel}
          onChange={setDigitsOnly}
        />
        {digitsOnly && (
          <p className="pl-6 text-muted-foreground text-xs">
            {strings.digitsOnlyNote}
          </p>
        )}
      </div>
      <ConverterCard
        copied={copiedCard === "super"}
        copiedLabel={strings.copied}
        copyLabel={strings.copy}
        exampleHint={
          digitsOnly ? strings.superscriptDigitsHint : strings.superscriptHint
        }
        id="superscript-input"
        input={superInput}
        label={strings.superscriptLabel}
        onCopy={() => handleCopy("super", superOutput)}
        onInputChange={setSuperInput}
        output={superOutput}
        placeholder={strings.resultPlaceholder}
      />
      <ConverterCard
        copied={copiedCard === "sub"}
        copiedLabel={strings.copied}
        copyLabel={strings.copy}
        exampleHint={
          digitsOnly ? strings.subscriptDigitsHint : strings.subscriptHint
        }
        id="subscript-input"
        input={subInput}
        label={strings.subscriptLabel}
        onCopy={() => handleCopy("sub", subOutput)}
        onInputChange={setSubInput}
        output={subOutput}
        placeholder={strings.resultPlaceholder}
      />
    </section>
  );
}
